import { types as t, destroy, flow } from 'mst-effect';
import Router from 'next/router';
import { checkDefaultFilters, compileAPIFilters, defaultFiltersValidator } from 'modules/InputsManager/helpers';
import { InputSearchStorageMethods } from 'hooks/useInputSearch';
import { InputQuery, deserializeConcept, SmartSearchState, SearchBarState } from 'modules/InputsManager/types';
import {
  InputRank,
  VisualUrlRank,
  VisualBase64Rank,
  rankIsVisualUrl,
  rankIsVisualImageBase64,
  InputIdSimilarityRank,
  rankIsVisualInputId,
  RankByPublicConcept,
  rankIsByPublicConcept,
  rankIsByAppConcept,
  rankIsTextSimilarityRank,
  TextSimilarityRank,
  RankByAppConcept,
} from 'modules/InputsManager/models/InputRank';
import { SearchInputBarStore } from 'modules/InputsManager/models/SearchInputBarStore';
import { ConceptFilter, ConceptFiltersStore } from 'modules/ConceptFilterStore';
import { DatasetFilter, DatasetFiltersStore } from 'modules/InputsManager/models/DatasetFiltersStore';
import { InputTypeFilter, InputsTypeFiltersStore } from 'modules/InputsManager/models/InputsTypeFiltersStore';
import { StatusFilterStore } from 'modules/InputsManager/models/StatusFilterStore';
import { LabelerFilterStore } from 'modules/InputsManager/models/LabelerFilterStore';

const FiltersSchema = {
  userOrOrgId: t.maybe(t.string),
  appId: t.maybe(t.string),
  ranks: t.array(InputRank),
  metadataFilter: t.maybe(t.frozen<CF.API.Metadata>()),
  selections: t.array(t.number),
};

export const FiltersStore = t
  .compose(
    SearchInputBarStore,
    ConceptFiltersStore,
    DatasetFiltersStore,
    InputsTypeFiltersStore,
    t.model('FiltersStore', FiltersSchema),
    StatusFilterStore,
    LabelerFilterStore,
  )
  .views((self) => {
    function _query(): InputQuery | undefined {
      const {
        metadataFilter,
        unlabeledFilter,
        unassignedFilter,
        conceptFilters,
        datasetFilters,
        inputTypeFilters,
        conceptsFilterOperator,
        statusFilterValue,
        labelerFilterValue,
      } = self;
      const filters = compileAPIFilters({
        metadataFilter,
        unlabeledFilter,
        unassignedFilter,
        conceptFilters,
        datasetFilters,
        inputTypeFilters,
        conceptsFilterOperator,
        statusFilterValue,
        labelerFilterValue,
      });
      const ranks = self.ranks.map((rank) => rank.toAPI());
      const { userOrOrgId, appId } = self;
      if (!userOrOrgId || !appId) return undefined;

      const q: InputQuery = {
        filters,
        ranks,
        userOrOrgId,
        appId,
      };
      return q;
    }

    return {
      get hasFilters(): boolean {
        const q = _query();
        const filters = q?.filters || [];

        // If all the filters are the default ones, it is similar to having 'no filters'
        if (filters.length === defaultFiltersValidator.length && checkDefaultFilters(filters)) return false;

        return filters.length > 0;
      },
      get isVisualUrlPresent(): boolean {
        return self.ranks.some((rank) => rankIsVisualUrl(rank));
      },
      get isImageBase64RankPresent(): boolean {
        return self.ranks.some((rank) => rankIsVisualImageBase64(rank));
      },
      get isVisualInputIdRank(): boolean {
        return self.ranks.some((rank) => rankIsVisualInputId(rank));
      },
      get isPublicConceptRank(): boolean {
        return self.ranks.some((rank) => rankIsByPublicConcept(rank));
      },
      get isTextSimilarityRank(): boolean {
        return self.ranks.some((rank) => rankIsTextSimilarityRank(rank));
      },
      get isAppConceptRank(): boolean {
        return self.ranks.some((rank) => rankIsByAppConcept(rank));
      },
      get isVisualRank() {
        return self.ranks.find((rank) => {
          return rankIsVisualUrl(rank) || rankIsVisualImageBase64(rank) || rankIsVisualInputId(rank);
        });
      },
      get visualSearchRank() {
        return self.ranks.find((rank): boolean => {
          return (
            rankIsVisualUrl(rank) ||
            rankIsVisualImageBase64(rank) ||
            rankIsVisualInputId(rank) ||
            rankIsTextSimilarityRank(rank) ||
            rankIsByPublicConcept(rank)
          );
        });
      },
    };
  })
  .actions((self) => ({
    setRankSearchInProcess(isInProcess: boolean) {
      self.isRankSearchInProcess = isInProcess;
    },
    setIsRefetchInputs(isRefetchInputs: typeof self.isRefetchInputs) {
      self.isRefetchInputs = isRefetchInputs;
    },
  }))
  .actions((self) => {
    // pull these out so we can call them when overriding
    const { addConceptFilter, setDatasetFilter, addInputsTypeFilters } = self;

    function _deleteRank(): void {
      if (self.visualSearchRank) {
        self.ranks.forEach((rank) => {
          destroy(rank);
        });
      }
    }

    function _deleteVisualUrlRank(): void {
      if (self.isVisualUrlPresent) {
        self.ranks.forEach((rank) => {
          if (rankIsVisualUrl(rank)) {
            destroy(rank);
          }
        });
      }
    }

    function _deleteVisualImageBase64(): void {
      if (self.isImageBase64RankPresent) {
        self.ranks.forEach((rank) => {
          if (rankIsVisualImageBase64(rank)) {
            destroy(rank);
          }
        });
      }
    }

    function _deleteVisualInputIdRank(): void {
      if (self.isVisualInputIdRank) {
        self.ranks.forEach((rank) => {
          if (rankIsVisualInputId(rank)) {
            destroy(rank);
          }
        });
      }
    }

    function _deletePublicConceptRank(): void {
      if (self.isPublicConceptRank) {
        self.ranks.forEach((rank) => {
          if (rankIsByPublicConcept(rank)) {
            destroy(rank);
          }
        });
      }
    }

    function _deleteAppConceptRank(): void {
      if (self.isAppConceptRank) {
        self.ranks.forEach((rank) => {
          if (rankIsByAppConcept(rank)) {
            destroy(rank);
          }
        });
      }
    }

    function _deleteTextSimilarityRank(): void {
      if (self.isTextSimilarityRank) {
        self.ranks.forEach((rank) => {
          if (rankIsTextSimilarityRank(rank)) {
            destroy(rank);
          }
        });
      }
    }
    const _clearSelections = (): void => {
      self.runInAction(() => {
        self.selections.replace([]);
      });
    };

    return {
      toggleSelection(index: number) {
        if (self.selections.includes(index)) {
          self.selections.remove(index);
        } else {
          self.selections.push(index);
        }
      },
      setInputsSelection(inputIndices: number[]) {
        self.selections.replace(inputIndices);
      },

      /**
       * @override
       *
       * Override to clear selections
       */
      addConceptFilter(filter: ConceptFilter) {
        _clearSelections();
        addConceptFilter(filter);
      },

      /**
       * @override
       *
       * Override to clear selections
       */
      addInputsTypeFilters(filter: InputTypeFilter) {
        _clearSelections();
        // This function is called from InputsTypeFiltersStore, which is composed in this store.
        addInputsTypeFilters(filter);
      },

      /**
       * @override
       *
       * Override to clear selections
       */
      setDatasetFilter(filter: DatasetFilter) {
        _clearSelections();
        setDatasetFilter(filter);
      },

      /**
       * Add a metadata to the filters
       */
      addMetadataFilter(metadata?: CF.API.Metadata) {
        self.metadataFilter = metadata;
      },

      /**
       * Remove a metadata from the filters
       */
      removeMetadataFilter() {
        self.metadataFilter = undefined;
      },

      /**
       * Clear all filters
       */
      clearFilters() {
        _clearSelections();
        self.metadataFilter = undefined;
        self.clearConceptFilters();
        self.clearDatasetFilters();
        self.clearInputTypeFilters();
        self.setShouldShowUnlabeled(false);
        self.setShouldShowUnassigned(false);
      },

      /**
       * Add the visual url rank
       */
      addVisualUrlRank(url: string) {
        _clearSelections();
        _deleteRank();

        if (self.isVisualUrlPresent) {
          _deleteVisualUrlRank();
        }
        self.ranks.push(VisualUrlRank.create({ url }));
      },

      /**
       * Remove the visual url rank
       */
      removeVisualUrlRank() {
        _clearSelections();
        _deleteVisualUrlRank();
      },

      /**
       * Add the visual image base64 rank.
       */
      addVisualImageBase64(base64: string) {
        _clearSelections();
        _deleteRank();

        const { router } = Router;
        if (router) {
          router.push(
            {
              query: { ...router.query },
            },
            undefined,
            { shallow: true },
          );
        }
        if (self.isImageBase64RankPresent) {
          _deleteVisualImageBase64();
        }
        self.ranks.push(VisualBase64Rank.create({ base64 }));
      },

      /**
       * Remove the visual image base64 rank
       */
      removeVisualImageBase64() {
        _clearSelections();
        _deleteVisualImageBase64();

        const { router } = Router;
        if (router) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { ...restQueryParams } = router.query;
          router.push(
            {
              query: { ...restQueryParams },
            },
            undefined,
            { shallow: true },
          );
        }
      },

      /**
       * Add a visual search rank with input ID
       */
      // eslint-disable-next-line func-names
      addVisualSearchRankWithInputId: flow(function* (inputId: string) {
        _clearSelections();
        _deleteRank();

        self.setRankSearchInProcess(true);
        self.setIsRefetchInputs(true);

        const { router } = Router;
        if (router) {
          yield router.push(
            {
              query: { ...router.query, rankId: inputId },
            },
            undefined,
            { shallow: true },
          );

          if (self.isVisualInputIdRank) {
            _deleteVisualInputIdRank();
          }
          self.ranks.push(InputIdSimilarityRank.create({ inputId }));
        }
      }),

      addTextSimilarityRank(inputText: string) {
        _clearSelections();
        _deleteRank();

        if (self.isTextSimilarityRank) {
          _deleteTextSimilarityRank();
        }
        self.ranks.push(TextSimilarityRank.create({ inputText }));
      },

      removeTextSimilarityRank() {
        _clearSelections();
        _deleteTextSimilarityRank();
      },

      /**
       * Remove visual search rank with input ID
       */

      removeVisualSearchRankWithInputId() {
        _deleteVisualInputIdRank();
        const { router } = Router;
        if (router) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { rankId, ...restQueryParams } = router.query;
          router.push(
            {
              query: { ...restQueryParams },
            },
            undefined,
            { shallow: true },
          );
        }
      },

      /**
       * Remove all selections
       */
      clearSelections() {
        _clearSelections();
      },

      /**
       *  Remove any applied visual search rank
       */
      removeVisualSearchRank() {
        _clearSelections();

        if (self.isVisualUrlPresent) {
          return this.removeVisualUrlRank();
        }

        if (self.isImageBase64RankPresent) {
          return this.removeVisualImageBase64();
        }

        if (self.isVisualInputIdRank) {
          return this.removeVisualSearchRankWithInputId();
        }

        if (self.isPublicConceptRank) {
          return this.removePublicConceptRank();
        }
        if (self.isTextSimilarityRank) {
          return this.removeTextSimilarityRank();
        }

        return null;
      },

      sortResultByConceptRank() {
        _clearSelections();

        _deletePublicConceptRank();
        _deleteAppConceptRank();

        self.publicConcepts.forEach((publicConcept) => {
          self.ranks.push(RankByPublicConcept.create({ publicConcept }));
        });

        self.appConcepts.forEach((appConcept) => {
          self.ranks.push(RankByAppConcept.create({ appConcept }));
        });
      },

      removePublicConceptRank() {
        _clearSelections();
        _deletePublicConceptRank();
        self.clearSearchToken();
      },

      /**
       *
       * This function will clear everything in the search bar
       */
      async clearSearchBar(inputSearch: InputSearchStorageMethods) {
        if (self.isPublicConceptRank || self.isTextSimilarityRank || self.isAppConceptRank) {
          self.setSearchText('');
          const smartSearch = await inputSearch.get('smartSearch');
          inputSearch.saveSearchParameters({
            smartSearch: {
              ...smartSearch,
              searchBarState: SearchBarState.Editing,
              searchText: '',
              searchTokens: [],
              publicConcepts: [],
              appConcepts: [],
            },
            textSearches: [],
          });
        }
        if (self.isImageBase64RankPresent) {
          inputSearch.clear('base64');
        }
        if (
          self.isPublicConceptRank ||
          self.isTextSimilarityRank ||
          self.isAppConceptRank ||
          self.isImageBase64RankPresent ||
          self.isVisualInputIdRank
        ) {
          self.setRankSearchInProcess(true);
        }
        this.removeVisualSearchRank();
      },
    };
  })
  .actions((self) => ({
    applySmartSearchState(state: SmartSearchState) {
      self.publicConcepts.replace(state.publicConcepts.map((sc) => deserializeConcept(sc)));
      self.appConcepts.replace(state.appConcepts.map((sc) => deserializeConcept(sc)));
      self.searchTokens.replace(state.searchTokens);
      self.setSearchBarInteractionState(state.searchBarState);
      self.sortResultByConceptRank();
    },

    loadRanksFromUrl() {
      const { router } = Router;
      if (router && router.query) {
        const { rankId: inputId, url } = router.query as { rankId: string; url: string };
        /* istanbul ignore if */
        if (url) {
          self.addVisualUrlRank(url);
        }
        /* istanbul ignore if */
        if (inputId) {
          self.addVisualSearchRankWithInputId(inputId);
        }
      }
    },
  }))
  .actions((self) => ({
    async afterCreate() {
      self.loadRanksFromUrl();
    },
  }));
